import * as React from 'react';
import { FieldWrapper } from '@progress/kendo-react-form';
import { FloatingLabel, Error, Hint } from '@progress/kendo-react-labels';
import { DropDownList } from '@progress/kendo-react-dropdowns';
// eslint-disable-next-line no-unused-vars
import BaseFormControl from './BaseFormControl';

/**
 * A dropdown list form field.
 *
 * @typedef {React.ComponentProps<BaseFormControl>} BaseFieldRenderProps
 * @typedef {React.PropsWithoutRef<DropDownList>} TelerikDropDownListProps
 *
 * @param {BaseFieldRenderProps & TelerikDropDownListProps} fieldRenderProps
 * @returns {React.ReactElement} Dropdown list form field.
 */
const FormDropDownList = (fieldRenderProps) => {
  const {
    validationMessage,
    touched,
    label,
    id,
    name,
    valid,
    disabled,
    hint,
    wrapperStyle,
    readOnly,
    value,
    optional,
    ...others
  } = fieldRenderProps;

  const editorId = id ?? name;

  const editorRef = React.useRef(null);
  const showValidationMessage = touched && validationMessage;
  const showHint = !showValidationMessage && hint;
  const hintId = showHint ? `${editorId}_hint` : '';
  const errorId = showValidationMessage ? `${editorId}_error` : '';
  const labelId = label ? `${editorId}_label` : '';

  return (
    <FieldWrapper style={wrapperStyle}>
      <FloatingLabel
        label={label}
        id={labelId}
        optional={optional}
        editorId={editorId}
        editorValid={valid}
        editorDisabled={disabled}
        editorValue={value}
        {...(readOnly ? { className: 'label-readonly' } : {})}
      >
        <DropDownList
          ariaLabelledBy={labelId}
          ariaDescribedBy={`${hintId} ${errorId}`}
          ref={editorRef}
          valid={valid}
          id={editorId}
          disabled={disabled}
          readOnly={readOnly}
          value={value}
          optional={optional}
          name={name}
          {...others}
        />

      </FloatingLabel>
      {showHint && <Hint id={hintId}>{hint}</Hint>}
      {showValidationMessage && <Error id={errorId}>{validationMessage}</Error>}
    </FieldWrapper>
  );
};

export default FormDropDownList;
